import {
  BacteriaReport,
  BacteriaResearch,
  BacteriaResearchSummary,
  LabMicrobiomeTestResult,
  MicrobiomeTestResult,
  Research,
  Taxonomy,
  uuid,
} from '@snapi/types';
import { TaxonomyUtils } from './taxonomy-utils';

const extractByTaxonomy = (
  results: LabMicrobiomeTestResult[],
  researches: Research[],
  level: Taxonomy
): {
  [key in string]: {
    research: Research;
    percentage: number;
    related: LabMicrobiomeTestResult[];
  };
} => {
  const resultByTaxonomy = results.reduce(
    (agg: { [key in string]: LabMicrobiomeTestResult[] }, result) => {
      const name = TaxonomyUtils.nameOnLevel(result.taxonomy, level);
      if (name) {
        if (agg[name]) {
          agg[name].push(result);
        } else {
          agg[name] = [result];
        }
      }
      return agg;
    },
    {}
  );

  return researches
    .filter((research) => research.taxonomy === level)
    .reduce(
      (
        agg: {
          [key in string]: {
            research: Research;
            percentage: number;
            related: LabMicrobiomeTestResult[];
          };
        },
        research
      ) => {
        const related = resultByTaxonomy[research.name];
        if (related) {
          const percentage = Object.values(related).reduce(
            (agg, { percentage }) => agg + percentage,
            0
          );
          agg[research.name] = {
            research,
            percentage,
            related,
          };
        }
        return agg;
      },
      {}
    );
};

const fromResults = (
  results: LabMicrobiomeTestResult[],
  research: Research[]
): MicrobiomeTestResult[] => {
  const speciesByResearch = extractByTaxonomy(
    results,
    research,
    Taxonomy.species
  );

  const genusByResearch = extractByTaxonomy(results, research, Taxonomy.genus);

  const familyByResearch = extractByTaxonomy(
    results,
    research,
    Taxonomy.family
  );

  return Object.values(speciesByResearch)
    .map<MicrobiomeTestResult>(({ research, percentage }) => ({
      research,
      classified: percentage,
      percentage,
      unclassified: 0,
      other: 0,
      disruptive: 0,
      protective: 0,
      neutral: 0,
      unknown: 0,
      [research.category]: percentage,
    }))
    .concat(
      Object.values(genusByResearch).map<MicrobiomeTestResult>(
        ({ research, percentage, related }) => {
          const classified = related.reduce(
            (sum, result) =>
              result.taxonomy.species ? sum + result.percentage : sum,
            0
          );
          const unclassified = percentage - classified;

          const summary = related
            .filter((result) =>
              TaxonomyUtils.nameOnLevel(result.taxonomy, Taxonomy.species)
            )
            .reduce(
              (agg, result) => {
                const key = TaxonomyUtils.nameOnLevel(
                  result.taxonomy,
                  Taxonomy.species
                );
                const species = speciesByResearch[key];
                if (species) {
                  agg[species.research.category] += result.percentage;
                } else {
                  agg[research.category] += result.percentage;
                }
                return agg;
              },
              {
                protective: 0,
                disruptive: 0,
                neutral: 0,
                unknown: 0,
                [research.category]: unclassified,
              }
            );

          const otherClassified = related.reduce((sum, result) => {
            const key = TaxonomyUtils.nameOnLevel(
              result.taxonomy,
              Taxonomy.species
            );
            return key && speciesByResearch[key]
              ? sum - result.percentage
              : sum;
          }, classified);

          const other = otherClassified < 0.000001 ? 0 : otherClassified;

          return {
            research,
            percentage,
            classified: percentage - (unclassified + other),
            unclassified,
            other,
            ...summary,
          };
        }
      )
    )
    .concat(
      Object.values(familyByResearch).map<MicrobiomeTestResult>(
        ({ research, percentage, related }) => {
          const classified = related.reduce(
            (sum, result) =>
              result.taxonomy.genus ? sum + result.percentage : sum,
            0
          );

          const unclassified = percentage - classified;

          const summary = related
            .filter((result) =>
              TaxonomyUtils.nameOnLevel(result.taxonomy, Taxonomy.genus)
            )
            .reduce(
              (agg, result) => {
                const key = TaxonomyUtils.nameOnLevel(
                  result.taxonomy,
                  Taxonomy.genus
                );
                const genus = genusByResearch[key];
                if (genus) {
                  agg[genus.research.category] += result.percentage;
                } else {
                  agg[research.category] += result.percentage;
                }
                return agg;
              },
              {
                protective: 0,
                disruptive: 0,
                neutral: 0,
                unknown: 0,
                [research.category]: unclassified,
              }
            );

          const other = related.reduce((sum, result) => {
            const key = TaxonomyUtils.nameOnLevel(
              result.taxonomy,
              Taxonomy.genus
            );
            return key && genusByResearch[key] ? sum - result.percentage : sum;
          }, classified);

          return {
            research,
            percentage,
            classified,
            unclassified,
            other,
            ...summary,
          };
        }
      )
    )
    .sort((a, b) => b.percentage - a.percentage)
    .map((a) => ({
      ...a,
      other: Math.max(0, a.other),
      unclassified: Math.max(0, a.unclassified),
      classified: Math.max(0, a.classified),
      percentage: Math.max(0, a.percentage),
    }));
};

const extractSummary = (
  name: string,
  bacteria: BacteriaReport[],
  summary: MicrobiomeTestResult[]
): BacteriaResearchSummary | null => {
  const parent = bacteria.find(
    (result) => result.threshold.research?.name === name
  );

  if (!parent || !parent.threshold.research) {
    return null;
  }

  const species = summary.filter(
    (result) => result.research?.ancestor === parent?.threshold?.research?.id
  );

  return {
    bacteria: {
      research: parent.threshold.research,
      unclassified: parent.unclassified,
      classified: parent.classified,
      percentage: parent.percentage,
      other: parent.other,
      protective: parent.protective,
      disruptive: parent.disruptive,
      neutral: parent.neutral,
    },
    species,
  };
};

const groupByAncestor = (
  summary: MicrobiomeTestResult[]
): BacteriaResearchSummary[] => {
  const cardByAncestor = summary.reduce(
    (agg: { [key in uuid]: BacteriaResearchSummary }, result) => {
      const bacteria: BacteriaResearch = result;

      if (result.research.ancestor) {
        if (agg[result.research.ancestor]) {
          agg[result.research.ancestor].species.push(bacteria);
        } else {
          agg[result.research.ancestor] = {
            bacteria,
            species: [bacteria],
          };
        }
      } else {
        if (agg[result.research.id]) {
          agg[result.research.id].bacteria = bacteria;
        } else {
          agg[result.research.id] = {
            bacteria: bacteria,
            species: [],
          };
        }
      }
      return agg;
    },
    {}
  );

  return Object.values(cardByAncestor);
};

export const MicrobiomeTestResultUtils = {
  extractSummary,
  groupByAncestor,
  fromResults,
};
